import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    CalendarBlank,
    CaretLeft,
    CaretRight,
    Plus,
    Download,
    Trash,
    Car,
    ClipboardText,
    Wrench,
    CalendarX,
    PushPin,
    X,
} from "@phosphor-icons/react";
import DatePickerYmd from "@/components/DatePickerYmd";

const KIND_META = {
    shaken_expiry:   { label: "車検満了",   Icon: CalendarX,      color: "#ffb547" },
    inspection:      { label: "点検実施",   Icon: ClipboardText,  color: "#3aaeff" },
    maintenance:     { label: "整備実施",   Icon: Wrench,         color: "#3aaeff" },
    maintenance_due: { label: "整備予定",   Icon: Wrench,         color: "#b45dff" },
    manual:          { label: "イベント",   Icon: PushPin,        color: "#00ff9c" },
};

function monthRange(d) {
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    // pad to cover ~45 days so UI can see adjacent events
    const start = new Date(first);
    start.setDate(start.getDate() - ((first.getDay() + 7) % 7)); // Sunday start
    const end = new Date(last);
    end.setDate(end.getDate() + (6 - last.getDay()));
    return { first, last, start, end };
}

function fmtISO(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function groupByDate(events) {
    const map = {};
    for (const e of events) {
        (map[e.date] = map[e.date] || []).push(e);
    }
    return map;
}

export default function Calendar() {
    const { can, user } = useAuth();
    const canManage = can("calendar.manage") || user?.role === "super_admin" || user?.role === "company_admin";

    const [cursor, setCursor] = useState(() => new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ date: "", title: "", note: "", vehicle_id: "", color: "#00ff9c" });
    const [vehicles, setVehicles] = useState([]);

    const { start, end, first, last } = useMemo(() => monthRange(cursor), [cursor]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/calendar/events", {
                params: { start: fmtISO(start), end: fmtISO(end) },
            });
            setEvents(data.events || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "カレンダー取得失敗");
        } finally {
            setLoading(false);
        }
    }, [start, end]);

    const loadVehicles = useCallback(async () => {
        try {
            const { data } = await api.get("/vehicles");
            setVehicles(data || []);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadVehicles(); }, [loadVehicles]);

    const byDate = useMemo(() => groupByDate(events), [events]);

    const days = useMemo(() => {
        const d = [];
        const cur = new Date(start);
        while (cur <= end) {
            d.push(new Date(cur));
            cur.setDate(cur.getDate() + 1);
        }
        return d;
    }, [start, end]);

    const monthLabel = `${cursor.getFullYear()}年 ${cursor.getMonth() + 1}月`;
    const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    const today = () => setCursor(new Date());

    const openAdd = (dateStr) => {
        setForm({ date: dateStr || fmtISO(new Date()), title: "", note: "", vehicle_id: "", color: "#00ff9c" });
        setShowAdd(true);
    };

    const saveManual = async () => {
        if (!form.date || !form.title.trim()) {
            toast.error("日付とタイトルは必須です");
            return;
        }
        try {
            const payload = { ...form };
            if (!payload.vehicle_id) delete payload.vehicle_id;
            if (!payload.note) delete payload.note;
            if (!payload.color) delete payload.color;
            await api.post("/calendar/manual", payload);
            toast.success("イベントを追加しました");
            setShowAdd(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "作成に失敗しました");
        }
    };

    const deleteManual = async (ev) => {
        if (!ev.manual_id) return;
        if (!window.confirm(`「${ev.title}」を削除しますか？`)) return;
        try {
            await api.delete(`/calendar/manual/${ev.manual_id}`);
            toast.success("削除しました");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "削除失敗");
        }
    };

    const exportIcs = async () => {
        try {
            const res = await api.get("/calendar/export.ics", {
                params: { start: fmtISO(start), end: fmtISO(end) },
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `prc-cmc-${monthLabel}.ics`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("iCal エクスポート失敗");
        }
    };

    const todayIso = fmtISO(new Date());
    const selectedEvents = selectedDate ? (byDate[selectedDate] || []) : [];

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline !text-[var(--prc-green)]">// FLEET CALENDAR · 車両管理スケジュール</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight text-[var(--prc-ink-2)] mt-1">カレンダー</h1>
                    <p className="text-sm text-[var(--prc-muted)] mt-1">車検満了・点検履歴・整備予定・手動イベントを一元表示</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={exportIcs} className="prc-btn-ghost inline-flex items-center gap-1.5" data-testid="calendar-export">
                        <Download size={16} /> iCal 出力
                    </button>
                    {canManage && (
                        <button onClick={() => openAdd(selectedDate)} className="prc-btn-primary inline-flex items-center gap-1.5" data-testid="calendar-add-event">
                            <Plus size={16} /> イベント追加
                        </button>
                    )}
                </div>
            </div>

            {/* Month header */}
            <div className="flex items-center justify-between border border-[var(--prc-border-strong)] bg-[var(--prc-surface-2)] px-3 py-2 rounded-sm">
                <div className="flex items-center gap-2">
                    <button onClick={prev} className="prc-btn-ghost !px-2 !py-1" data-testid="cal-prev"><CaretLeft size={16} /></button>
                    <button onClick={today} className="prc-btn-ghost !px-3 !py-1 text-xs font-mono uppercase tracking-wider" data-testid="cal-today">Today</button>
                    <button onClick={next} className="prc-btn-ghost !px-2 !py-1" data-testid="cal-next"><CaretRight size={16} /></button>
                </div>
                <div className="font-mono text-sm font-bold tracking-wider text-[var(--prc-ink-2)]" data-testid="cal-month-label">
                    {monthLabel}
                </div>
                <div className="text-xs text-[var(--prc-muted)] font-mono">
                    {loading ? "LOADING…" : `${events.length} EVENTS`}
                </div>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-px bg-[var(--prc-border)] border border-[var(--prc-border-strong)] rounded-sm overflow-hidden">
                {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
                    <div key={w} className={`bg-[var(--prc-surface-2)] px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest ${i === 0 ? "text-[var(--prc-red)]" : i === 6 ? "text-[#3aaeff]" : "text-[var(--prc-muted)]"}`}>
                        {w}
                    </div>
                ))}
                {/* Days */}
                {days.map((d) => {
                    const iso = fmtISO(d);
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isToday = iso === todayIso;
                    const isSelected = iso === selectedDate;
                    const dayEvents = byDate[iso] || [];
                    return (
                        <button
                            key={iso}
                            onClick={() => setSelectedDate(isSelected ? null : iso)}
                            data-testid={`cal-day-${iso}`}
                            className={`relative min-h-[88px] text-left bg-[var(--prc-surface)] px-1.5 py-1 hover:bg-[var(--prc-surface-2)] transition
                                ${inMonth ? "" : "opacity-40"}
                                ${isSelected ? "ring-1 ring-[var(--prc-green)] z-10" : ""}
                            `}
                        >
                            <div className={`flex items-center justify-between text-[11px] font-mono ${isToday ? "text-[var(--prc-green)] font-bold" : "text-[var(--prc-ink-2)]"}`}>
                                <span>{d.getDate()}</span>
                                {isToday && <span className="text-[8px] uppercase tracking-wider">TODAY</span>}
                            </div>
                            <div className="mt-1 space-y-0.5">
                                {dayEvents.slice(0, 3).map((ev) => {
                                    const meta = KIND_META[ev.kind] || KIND_META.manual;
                                    const color = ev.color || meta.color;
                                    return (
                                        <div
                                            key={ev.id}
                                            className="text-[10px] px-1 py-0.5 rounded-sm truncate border-l-2"
                                            style={{ borderLeftColor: color, background: `${color}1a`, color: "#e5e7eb" }}
                                            title={ev.title}
                                        >
                                            {ev.title}
                                        </div>
                                    );
                                })}
                                {dayEvents.length > 3 && (
                                    <div className="text-[9px] text-[var(--prc-muted)] font-mono">+{dayEvents.length - 3} more</div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Selected day detail */}
            {selectedDate && (
                <div className="prc-card p-5" data-testid="cal-day-detail">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <p className="prc-label-overline">SELECTED DAY</p>
                            <h2 className="font-bold text-lg text-[var(--prc-ink-2)]">{selectedDate}</h2>
                        </div>
                        <div className="flex gap-2">
                            {canManage && (
                                <button onClick={() => openAdd(selectedDate)} className="prc-btn-ghost inline-flex items-center gap-1 text-xs">
                                    <Plus size={16} /> この日に追加
                                </button>
                            )}
                            <button onClick={() => setSelectedDate(null)} className="prc-btn-ghost !px-2" aria-label="close"><X size={16} /></button>
                        </div>
                    </div>
                    {selectedEvents.length === 0 ? (
                        <p className="text-sm text-[var(--prc-muted)]">この日のイベントはありません。</p>
                    ) : (
                        <ul className="space-y-2">
                            {selectedEvents.map((ev) => {
                                const meta = KIND_META[ev.kind] || KIND_META.manual;
                                const color = ev.color || meta.color;
                                return (
                                    <li key={ev.id} className="flex items-start gap-3 border border-[var(--prc-border)] px-3 py-2 rounded-sm" data-testid={`cal-event-${ev.id}`}>
                                        <div className="mt-0.5">
                                            <meta.Icon size={24} style={{ color }} weight="fill" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-[var(--prc-ink-2)]">{ev.title}</div>
                                            {ev.subtitle && <div className="text-xs text-[var(--prc-muted)] mt-0.5">{ev.subtitle}</div>}
                                            <div className="text-[10px] font-mono tracking-wider uppercase text-[var(--prc-muted)] mt-1">{meta.label} · {ev.source === "manual" ? "MANUAL" : "AUTO"}</div>
                                        </div>
                                        {ev.source === "manual" && canManage && (
                                            <button onClick={() => deleteManual(ev)} className="text-[var(--prc-red)] hover:opacity-80 p-1" data-testid={`cal-delete-${ev.manual_id}`}>
                                                <Trash size={16} />
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)]">
                {Object.entries(KIND_META).map(([k, m]) => (
                    <span key={k} className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: m.color }} />
                        {m.label}
                    </span>
                ))}
            </div>

            {/* Add event modal */}
            {showAdd && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="cal-add-modal">
                    <div className="bg-[var(--prc-surface)] border border-[var(--prc-border-strong)] rounded-sm w-full max-w-lg">
                        <div className="flex items-center justify-between border-b border-[var(--prc-border)] px-4 py-3">
                            <div className="flex items-center gap-2">
                                <CalendarBlank size={24} className="text-[var(--prc-green)]" />
                                <h3 className="font-bold text-[var(--prc-ink-2)]">イベント追加</h3>
                            </div>
                            <button onClick={() => setShowAdd(false)} className="prc-btn-ghost !px-2"><X size={16} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div>
                                <label className="prc-label-overline">日付 *</label>
                                <DatePickerYmd
                                    value={form.date}
                                    onChange={(val) => setForm({ ...form, date: val })}
                                    required
                                    testid="cal-form-date"
                                />
                            </div>
                            <div>
                                <label className="prc-label-overline">タイトル *</label>
                                <input className="prc-input w-full" value={form.title}
                                       onChange={(e) => setForm({ ...form, title: e.target.value })}
                                       placeholder="例: 全社ミーティング / タイヤ交換"
                                       data-testid="cal-form-title" />
                            </div>
                            <div>
                                <label className="prc-label-overline">メモ</label>
                                <textarea className="prc-input w-full min-h-[64px]" value={form.note}
                                          onChange={(e) => setForm({ ...form, note: e.target.value })}
                                          placeholder="任意"
                                          data-testid="cal-form-note" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="prc-label-overline">関連車両</label>
                                    <select className="prc-input w-full" value={form.vehicle_id}
                                            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                                            data-testid="cal-form-vehicle">
                                        <option value="">(なし)</option>
                                        {vehicles.map((v) => (
                                            <option key={v.id} value={v.id}>{v.plate_number} / {v.make} {v.model}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="prc-label-overline">色</label>
                                    <input type="color" className="prc-input w-full h-10" value={form.color}
                                           onChange={(e) => setForm({ ...form, color: e.target.value })}
                                           data-testid="cal-form-color" />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-[var(--prc-border)] px-4 py-3">
                            <button onClick={() => setShowAdd(false)} className="prc-btn-ghost">キャンセル</button>
                            <button onClick={saveManual} className="prc-btn-primary" data-testid="cal-form-save">保存</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
